import { expVal, expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type { EditorContentUnifiedQuery$variables } from './__generated__/EditorContentUnifiedQuery.graphql.ts';

export type EditorContentStatus = 'current' | 'archived' | 'draft';

// Returns the published node for a published page and the draft node for a never-published one.
// Order matters: it is part of the Relay root storage key.
export const EDITOR_CONTENT_STATUSES: readonly EditorContentStatus[] = [
	'current',
	'archived',
	'draft',
];

export type EditorContentUnifiedQueryVariableOptions = {
	contentId: string;
	draftShareId?: string | null;
	status?: readonly EditorContentStatus[];
	spaceKey?: string | null;
	versionOverride?: number | null;
	isSSR?: boolean;
};

export type EditorContentUnifiedQueryVariables = {
	contentId: string;
	status: readonly EditorContentStatus[];
	draftShareId?: string;
	spaceKey?: string | null;
	versionOverride?: number | null;
	isSSR?: boolean;
};

export const createEditorContentUnifiedQueryVariables = ({
	contentId,
	draftShareId,
	status = EDITOR_CONTENT_STATUSES,
	spaceKey,
	versionOverride,
	isSSR,
}: EditorContentUnifiedQueryVariableOptions): EditorContentUnifiedQueryVariables => ({
	contentId,
	status,
	// "" would stay in the storage key and cost the backend a permission check; omit instead
	...(draftShareId ? { draftShareId } : {}),
	...(spaceKey !== undefined ? { spaceKey } : {}),
	...(versionOverride !== undefined ? { versionOverride } : {}),
	...(isSSR !== undefined ? { isSSR } : {}),
});

/**
 * The variables of EditorContentUnifiedQuery itself: the shared root variables plus the route's
 * isLivePage and the conditional selections, which the legacy queries using the helper above do
 * not take.
 */
export const createEditorContentOperationVariables = ({
	contentId,
	cloudId,
	draftShareId,
	isLivePage,
}: {
	contentId: string;
	cloudId: string;
	draftShareId?: string | null;
	isLivePage: boolean;
}): EditorContentUnifiedQuery$variables => ({
	...createEditorContentUnifiedQueryVariables({ contentId, draftShareId }),
	cloudId,
	isLivePage,
	// The conditions of the legacy editor byline, Dev Docs banner, content state and collaborators
	// queries.
	...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_classification_context_frag_reader') &&
	expVal('confluence_exp_vp_document', 'isEnabled', false)
		? { includeClassification: true }
		: {}),
	...(expValEquals('confluence_classification_level_v2', 'isEnabled', true)
		? { useClassificationLevelV2: true }
		: {}),
	...(!isLivePage && isExperimentEnabled('confluence_dev_docs')
		? { includeDevDocsAncestors: true }
		: {}),
	// Edit Page shows the draft status, Live Page the published one.
	...(isLivePage ? { isContentStateDraft: false, includeContentStateLastUpdated: true } : {}),
	...(expValEquals('confluence_approvals_eap', 'isEnabled', true)
		? {
				includeContentStateApprovals: true,
				includeContentStateLastUpdated: true,
				contentStateLastUpdatedFormat: 'MILLIS' as const,
			}
		: {}),
	...(cloudId && expValEquals('dual_agent_avatar_card', 'isEnabled', true)
		? { includeCollaborators: true }
		: {}),
});
